import {
  BaseDto,
  CustomerDto,
  DriverDto,
  LocationDto,
  OperatorNotesDto,
  PlantDto,
  ProductDto,
  ProductLine,
  ProjectDto,
  TruckDetailsDto,
  UrlKeyDto,
  UserPermissionAccessDto,
  UserRegionDto,
  TruckDto,
  VoidFunc,
  TicketDto,
  UserPreferencesDto,
} from '@trucktrax/trucktrax-ts-common';
import { Action } from 'redux';
import {
  APP_SWITCHER_SELECTED,
  INCOMING_TICKETS_SELECTED,
  MESSAGE_SELECTED,
  NOTEBOOK_SELECTED,
  TICKETS_SELECTED,
} from './constants/appConstants';

export interface InputField {
  type: string;
  meta: {
    submitFailed?: boolean;
    error?: string;
    touched?: boolean;
  };
  label?: string;
  input?: any;
  name?: string;
}

export interface Permission {
  access: string;
  permissionUrl: string;
  permission: string;
}

export interface UserPermission {
  regions: { regionUrl: string }[];
  permissions: Permission[];
  groupUrl: string;
  tenantUrl: string;
}

export interface Marker {
  ticket: {
    url: string;
  };
}

export interface LogoutDriverInfo {
  firstName?: string;
  lastName?: string;
  driver?: {
    firstName?: string;
    lastName?: string;
    url?: string;
  };
  trackedObject?: {
    truckAlias?: string;
  };
}

export interface LogoutUserInfo {
  firstName?: string;
  lastName?: string;
  id?: string;
}

export interface UserPermissions {
  [permissionName: string]: UserPermissionAccessDto;
}

export interface UserPermissionsByRegion {
  [regionUrl: string]: UserPermissions;
}

export interface TicketStatusMarker {
  url: string;
  region: {
    url: string;
  };
}

export type ModalType = 'ERROR' | 'SUCCESS' | 'DEFAULT' | 'ACCENT' | 'FAIL';

export interface ModalOptions {
  modalType: ModalType;
  modalTitle?: string;
  modalBody: JSX.Element | string;
  modalOpen: boolean;
  isDeleteConfirmation?: boolean;
  acceptDialog?: VoidFunc;
  acceptText?: string;
  cancelText?: string;
  disabled?: boolean;
  noActions?: boolean;
}

export interface ScaleTraxSearchFilters {
  currentPlant?: PlantDto;
  currentTruck?: TruckDto;
  currentProduct?: ProductDto;
  currentCustomer?: CustomerDto;
  currentProject?: ProjectDto;
  startDate?: Date;
  endDate?: Date;
}

/**
 * The slice of the BE StatusEvent payload the IM FE actually reads. The
 * ambient ts-common shim types StatusEventDto as `any`, which let an
 * `as any` cast hide a real bug — see DEV-3883 review. Narrowing here gives
 * the reducer and selectors a real contract; extra fields the BE includes
 * are preserved structurally by spread merges where applicable.
 */
export interface IncomingTicketStatus {
  status: string;
  timestamp: string;
}

/**
 * DEV-4153: material-received acknowledgement stamped by
 * `POST /ticketing/api/tickets/{id}/material-received` (the ticketing BFF).
 * `materialReceivedBy` is a user URL (not a display name) — render it through
 * `ResolvedUserName`. Lives here until ts-common's TicketDto exports these
 * fields.
 */
export interface MaterialReceiptFields {
  materialReceivedBy?: UrlKeyDto;
  materialReceivedAt?: string;
}

/**
 * Ticket payload augmented with the BE's authoritative current workflow step.
 * Returned by `/tickets/with-current-status`, which appends `currentStatus`
 * to the standard TicketDto. Used by the Incoming Materials page; drop the
 * local declaration once ts-common exports this shape.
 */
export interface TicketWithCurrentStatusDto extends TicketDto, MaterialReceiptFields {
  currentStatus?: IncomingTicketStatus;
}

export interface IncomingMaterialsRegionPreferences {
  /**
   * plant ids unchecked in the IM Destination Plants filter. Unchecked ids are stored so a
   * plant created after the save, or newly in scope, is checked by default (PO decision
   * 2026-10-09); this deliberately differs from the main map's selectedPlantIds convention.
   * Absent when only product lines are unchecked.
   */
  deselectedPlantIds?: number[];
  /**
   * DEV-5225: product lines unchecked in the IM ticket panel's Product Line filter, stored
   * unchecked like the plants. Absent means every product line is checked.
   */
  deselectedProductLines?: ProductLine[];
}

export interface GeotraxUserPreferencesDto extends UserPreferencesDto {
  /**
   * keyed by region id (same string key as mapFilters), not product line — the
   * Incoming Materials plant list spans every product line in the region.
   */
  incomingMaterials?: Record<string, IncomingMaterialsRegionPreferences>;
}

/**
 * Route history rows carry the driver's ownership alongside the truck's, so an
 * external driver on one of our trucks (or the reverse) is distinguishable in
 * the results.
 *
 * `/routehistory` does NOT serve this field. It is populated client-side by
 * `annotateDriverOwnership`, which matches each route's `truck.driverUrl` against
 * the drivers the search form has already loaded. Left undefined when that
 * lookup finds nothing, and read as internal in that case.
 */
export interface RouteTruckDetailsDto extends TruckDetailsDto {
  driverIsExternal?: boolean;
}

export interface PositionDetailsDto extends BaseDto {
  source: UrlKeyDto;
  driver: DriverDto;
  region: UrlKeyDto;
  trackedObject: TruckDetailsDto;
  speed: number;
  bearing: number;
  location: LocationDto;
  active: boolean;
  timestamp: string;
  status: string;
  statusTimestamp: string;
  productLine: ProductLine;
  ticket?: TicketDto;
  plantLocation?: LocationDto;
}

export enum MapMarkerType {
  Driver = 'Driver',
  Truck = 'Truck',
  Order = 'Order',
  Plant = 'Plant',
}

export const MAP_TYPES = {
  HYBRID: 'hybrid',
  ROADMAP: 'roadmap',
  SATELLITE: 'satellite',
  TERRAIN: 'terrain',
} as const;

export type MapTypes = (typeof MAP_TYPES)[keyof typeof MAP_TYPES];

export interface MapMarkerData {
  positionDetails?: PositionDetailsDto;
  markerId: string;
  ticketUrl: string;
  lat: number;
  lng: number;
  isActive: boolean;
  status: string | null;
  isExternal: boolean | undefined;
  plantUrl?: string;
  driverUrl?: string;
  truckUrl?: string;
  truckAlias?: string;
  ticket?: TicketDto;
  truckDetails?: TruckDto;
  driverDetails?: DriverDto | null;
  type: MapMarkerType;
}

export interface MapMarker {
  id: string;
  markerId: string;
  name: string;
  text: string;
  type: MapMarkerType;
  data: MapMarkerData;
}

export interface AuthTokenPayload {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  tenantId: string;
  loginTime: string;
  primaryProductLine: ProductLine;
  secondaryProductLine: ProductLine;
  secondaryProductLines: ProductLine[];
  id: number;
  assignedRegions: UserRegionDto[];
  isPasswordTemporary: boolean;
  url: string;
  user: string;
  userFullName: string;
}

export interface HttpError {
  response?: {
    data?: {
      message?: string;
    };
    status?: number;
  };
}

export interface AssignedRegionDto {
  url?: string;
  name?: string;
  productLines?: ProductLine[];
}

export interface UserRegionWithName extends UserRegionDto {
  name?: string;
}

export type ConnectedFunction<T extends (...args: any) => any> = (
  ...args: Parameters<T>
) => ReturnType<T>;

export type ConnectedDispatchFunction<T extends (...args: any) => any> = (
  ...args: Parameters<T>
) => ReturnType<ReturnType<T>>;

/* traits */
export interface HasProductLines {
  primaryProductLine: ProductLine;
  secondaryProductLine: ProductLine;
}

export interface HasNameParts {
  firstName?: string;
  lastName?: string;
}

export interface HasUrl {
  url?: string;
}

export interface MaybeDefaultRegion {
  url: string;
  defaultRegion: boolean;
}

export type DropDownListItem = {
  label: string | undefined;
  key?: string;
  value?: HasUrl | MaybeDefaultRegion[];
  disabled?: boolean;
};

export interface HasName {
  name?: string;
}

export type HasNameAndUrl = HasName & HasUrl;

export interface HasDescription {
  description: string;
}

export interface IsRemovable {
  archived: boolean;
  deleted: boolean;
}

export interface HasId {
  id: number;
}

export interface MayHaveExternalId {
  externalId?: string;
}

export interface HasAddress {
  addressLineOne?: string;
  addressLineTwo?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export type SimpleEvent<T = string> = {
  target: {
    value: T;
  };
};

export type ZoneType = 'plant' | 'inqueue' | 'return';

export type SidebarTitleType =
  | typeof MESSAGE_SELECTED
  | typeof TICKETS_SELECTED
  | typeof INCOMING_TICKETS_SELECTED
  | typeof APP_SWITCHER_SELECTED
  | typeof NOTEBOOK_SELECTED;

export enum ScaleState {
  unknown = 0,
  connecting = 1,
  connected = 2,
  timedOut = 3,
  disconnected = 4,
}

export interface ReceivedWeight {
  scaleId: string;
  weight: number;
  isMoving: boolean;
  scaleState: ScaleState;
}

export interface ScaleHubState {
  connected: boolean;
}

export interface OperatorNotesPage {
  items: OperatorNotesDto[];
  plantUrl: string;
  page: number;
}

export enum TruckTraxApps {
  GeoTrax,
  Admin,
}

export interface AppPath {
  type: TruckTraxApps;
  name: string;
  horizontalIcon: string;
  verticalIcon: string;
  path: string;
}

export enum TicketStateSwitcherType {
  Tabs,
  DropDown,
}

export enum TicketStateValue {
  AllTickets,
  Closed,
  Voided,
  Open,
  Active,
}

export interface TicketListFilters {
  ticketStateValue: TicketStateValue;
  filterDateStart: Date;
  filterDateEnd: Date;
  searchTerm: string;
}

export type DateInputType = string | Date | number | null | undefined;

export enum LoadingTimeValue {
  FIVE = 5,
  TEN = 10,
  FIFTEEN = 15,
  TWENTY = 20,
  TWENTYFIVE = 25,
  THIRTY = 30,
}

export interface PayloadAction<TPayload, TString = string> extends Action<TString> {
  payload: TPayload;
}

/**
 * Mirrors the backend contract delivered by DEV-4351 (`/geotrax/api/notifications/email`).
 * The centralized distribution list is one row per recipient + notification type +
 * plant.
 *
 * Values are the `NotificationTopicType` member names: every TruckTrax backend runs
 * Newtonsoft's StringEnumConverter (registered globally in `AddTruckTrax`), so the
 * enum crosses the wire as its name, not its ordinal. The backend's `Unknown = 0`
 * sentinel is deliberately absent here — it is rejected as "Notification Type must
 * be specified", so the UI must never be able to send it.
 *
 * `NotificationTopicType` also carries `Checklists`, which is deliberately absent:
 * checklist notifications are no longer supported here. Nothing in the UI can send
 * that value, and any row still stored against it is dropped on read -- see
 * `SUPPORTED_NOTIFICATION_TYPES` in services/emailSubscriptionsService.
 */
export enum EmailNotificationType {
  IncomingMaterials = 'IncomingMaterials',
  Diversions = 'Diversions',
}

export const EMAIL_NOTIFICATION_TYPE_LABELS: Record<EmailNotificationType, string> = {
  [EmailNotificationType.IncomingMaterials]: 'Incoming Materials',
  [EmailNotificationType.Diversions]: 'Diversions',
};

/**
 * The contract the Email Notifications screen writes and reads: one row per
 * recipient + notification type + plant (DEV-4350/DEV-4351), served from
 * `/notifications/email`.
 *
 * Rows are denormalized, so configuring one address against two topics and six
 * plants writes twelve of them. The screen presents an address rather than a row --
 * see {@link EmailSubscriptionGroup} -- and expands back to this shape on save.
 *
 * Named distribution lists, which would let several addresses share one
 * subscription set, are a separate piece of work (DEV-4497). Nothing here assumes
 * them.
 */
export interface EmailNotificationSubscriptionDto {
  id?: number;
  url?: string;
  email: string;
  notificationType: EmailNotificationType;
  /** Region scope — always required. */
  region: UrlKeyDto;
  /** Plant scope — the plant within `region` the notification is about. */
  plant?: UrlKeyDto | null;
  active: boolean;
}

/**
 * One address's whole subscription set, which is what the admin table shows as a row.
 *
 * The API stores and returns the denormalized rows -- one per (email, type, scope) --
 * so an address subscribed to two topics across six plants is twelve rows. Admins
 * configure and read an address, not a row, so the screen regroups them here; see
 * `groupSubscriptions` in util/emailNotificationUtil.
 *
 * `subscriptions` is every live row behind the group, kept so a save can diff against
 * what exists and so each row can carry its own status. There is deliberately no
 * rollup `active` flag -- status is per row, and `groupStatus` in
 * util/emailNotificationUtil derives the three-state rollup the table column shows.
 */
export interface EmailSubscriptionGroup {
  /** Grouping key: the address, lowercased. Used as the table's row id. */
  key: string;
  /** The address as stored, in the casing the first row carried. */
  email: string;
  notificationTypes: EmailNotificationType[];
  regions: UrlKeyDto[];
  plants: UrlKeyDto[];
  subscriptions: EmailNotificationSubscriptionDto[];
}
